// src/components/auth/Login.js
import React, { useState } from 'react'; // Removed useEffect as it's not needed here anymore
import { PeraWalletConnect } from '@perawallet/connect';

// Import the configured Axios instance
import apiClient from '../../api'; // Verify this path is correct

// Import assets (ensure paths are correct)
import googleIcon from '../../assets/icons/branding_guideline_sample_nt_sq_sl.svg';
import peraIcon from '../../assets/icons/pera-logomark-black.svg';

// Initialize Pera Wallet connection
const peraWallet = new PeraWalletConnect();

// Destructure props for clarity
function Login({ openModal, onLoginSuccess }) {
  // State for form fields and messages
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Loading state

  // --- Helper Function to Handle Successful Login ---
  const handleLogin = (token, loginMethod) => {
    console.log(`Login Component: Storing token from ${loginMethod} and calling onLoginSuccess.`);
    localStorage.setItem('authToken', token);
    if (typeof onLoginSuccess === 'function') {
      onLoginSuccess(); // This should set isAuthenticated=true and close the modal
    } else {
      console.error("Login Component: onLoginSuccess is not a function!");
      setMessage("Login succeeded but failed to update application state.");
      setIsLoading(false); // Manually reset loading if onLoginSuccess fails
    }
    // No need to reset isLoading here if onLoginSuccess closes the modal,
    // otherwise, uncomment the line below:
    // setIsLoading(false);
  };

  // --- Helper Function to Handle API Errors ---
  const handleError = (error, method) => {
    console.error(`Login Component: ${method} Error:`, error.response || error.message || error);
    // Extract error message from backend response or use generic message
    const detail = error.response?.data?.detail || error.message || 'An unexpected error occurred.';
    setMessage(`Error: ${detail}`);
    setIsLoading(false); // Ensure loading state is always reset on error
  };

  // --- Email/Password Login Handler ---
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setWalletAddress('');
    setIsLoading(true);
    console.log("Login Component: Attempting Email Login..."); // Log start

    try {
      // Prepare data in 'application/x-www-form-urlencoded' format
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);
      // grant_type, scope, client_id, client_secret are often optional for password flow
      // params.append('grant_type', 'password');
      // params.append('scope', '');
      // params.append('client_id', '');
      // params.append('client_secret', '');

      console.log("Login Component: Sending request to /token with params:", params.toString()); // Log params

      // Use apiClient instance (interceptor won't add auth header yet)
      const response = await apiClient.post('/token', params, {
         headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      console.log("Login Component: Received response from /token:", response); // Log response

      if (response.data && response.data.access_token) {
        setMessage('Login successful!');
        handleLogin(response.data.access_token, 'Email');
      } else {
        console.error("Login Component: Access token not found in response", response.data);
        throw new Error('Access token not received from server.');
      }

    } catch (error) {
      handleError(error, 'Email Login');
    } finally {
      // Ensure isLoading is reset if handleLogin doesn't (e.g., if onLoginSuccess fails)
      // It's safer to reset it here, although it might cause a brief flicker if the modal closes immediately.
      // If the modal closing reliably unmounts the component, this might not be strictly needed.
       console.log("Login Component: Email Login finally block.");
       // Check if component might still be mounted before setting state
       // For simplicity, we'll just set it. If the modal closes, React handles unmounting.
       setIsLoading(false);
    }
  };

  // --- Google Login Handler ---
  const handleGoogleLogin = () => {
    setMessage('');
    setWalletAddress('');
    setIsLoading(true); // Show loading briefly
    console.log("Login Component: Attempting Google Login..."); // Log start

    // Construct the backend Google login URL
    const googleLoginUrl = `${apiClient.defaults.baseURL}/auth/google/login`;
    console.log("Login Component: Redirecting to Google Auth URL:", googleLoginUrl);

    // Redirect the user
    window.location.href = googleLoginUrl;
    // Note: setIsLoading(false) won't run here as the page redirects.
    // The loading state will reset when the user returns to the app.
  };

  // --- Pera Wallet Login Handler ---
  const handlePeraLogin = async () => {
    setMessage('');
    setWalletAddress('');
    setIsLoading(true);
    console.log("Login Component: Attempting Pera Wallet Login..."); // Log start

    try {
      console.log("Login Component: Connecting to Pera Wallet...");
      const accounts = await peraWallet.connect();
      if (!accounts || accounts.length === 0) {
        console.warn("Login Component: No Pera account selected or connection failed.");
        throw new Error("No Pera Wallet account selected or connection failed.");
      }
      const address = accounts[0];
      console.log("Login Component: Pera Wallet connected, address:", address);
      setWalletAddress(shortenAddress(address));

      console.log("Login Component: Sending request to /auth/pera/login with address:", address);
      // Use apiClient instance
      const response = await apiClient.post('/auth/pera/login', { address }); // Send address as JSON

      console.log("Login Component: Received response from /auth/pera/login:", response); // Log response

      if (response.data && response.data.access_token) {
        setMessage('Pera Wallet login successful!');
        handleLogin(response.data.access_token, 'Pera Wallet');
      } else {
         console.error("Login Component: Access token not found in Pera response", response.data);
        throw new Error('Access token not received from server after Pera login.');
      }

    } catch (error) {
      // Handle potential errors from Pera connection or API call
      if (error.message?.includes("Popup closed")) {
         console.warn("Login Component: Pera Wallet popup closed by user.");
         setMessage("Pera Wallet connection cancelled.");
         // No need to call handleError, just reset loading
         setIsLoading(false);
      } else {
         handleError(error, 'Pera Login'); // Handles other errors and resets loading
      }
    } finally {
      // Reset loading state only if it wasn't reset by error handling (like popup closed)
      // It's generally safer to reset in handleError or handleLogin.
      // If an uncaught error occurs before those, this ensures reset.
      console.log("Login Component: Pera Login finally block.");
      // Check if component might still be mounted
       setIsLoading(false); // Reset loading state as a safeguard
    }
  };

  // --- Utility Function ---
  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // --- Switch Form Handler ---
  const handleSwitchToRegister = () => {
    if (typeof openModal === 'function') {
      openModal('register');
    } else {
      console.error("Login Component: openModal is not a function when trying to switch!");
      setMessage("Error: Cannot switch form.");
    }
  };

  // --- Render Logic ---
  return (
    <div>
      <h2>Login</h2>
      {/* Email/Password Form */}
      <form onSubmit={handleEmailSubmit}>
        <div>
          <label htmlFor="login-email">Email:</label>
          <input
            type="email"
            id="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            autoComplete="username" // Add autocomplete hint
          />
        </div>
        <div>
          <label htmlFor="login-password">Password:</label>
          <input
            type="password"
            id="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
            autoComplete="current-password" // Add autocomplete hint
          />
        </div>
        <button type="submit" disabled={isLoading}>
          {/* Check isLoading state for button text/disabled status */}
          {isLoading ? 'Logging in...' : 'Login with email'}
        </button>
      </form>

      {/* Social/Wallet Logins */}
      <button className="google-btn" onClick={handleGoogleLogin} disabled={isLoading}>
        <img src={googleIcon} alt="Google" height="20"/> Login with Google
      </button>
      <button className="pera-btn" onClick={handlePeraLogin} disabled={isLoading}>
        <img src={peraIcon} alt="Pera Wallet" height="20"/> Connect Pera Wallet
      </button>

      {/* Switch to Register */}
      <p>Don't have an account? <button onClick={handleSwitchToRegister} disabled={isLoading}>Register here</button></p>

      {/* Display Messages */}
      {message && (
        // Use class names for styling based on message type
        <p className={message.toLowerCase().includes('successful') ? 'success-message' : message.toLowerCase().includes('error') ? 'error-message' : 'info-message'}>
          {message}
        </p>
      )}
      {walletAddress && (
        <p className="info-message">
          Connected Wallet: <strong>{walletAddress}</strong>
        </p>
      )}
    </div>
  );
}

export default Login;
